// Theme + helpers for the Closing-Team Coverage calendar
// (frontend_documentation/closing-coverage-calendar.md).
//
// Renders out-of-office (OOO) coverage entries and bank-holiday / wire-block
// days. OOO times are stored as UTC ISO with a per-entry `timezone` snapshot;
// we display and bucket each entry in that timezone.

import { addDays, format } from "date-fns";

import { slate, status, teal } from "../../theme/colors";

// ── Palette ──────────────────────────────────────────────────────────────────
// Neutrals + the accent come from the theme (src/theme/colors.js); colour on
// this surface is spent on readiness / reason / wire-block, so the chrome is
// slate and the one accent is the action teal.
export const C = {
  bg: slate[50],
  surface: "#ffffff",
  ink: slate[900],
  sub: slate[600],
  faint: slate[400],
  line: slate[200],
  lineSoft: slate[100],
  accent: teal[600],
  accentSoft: teal[50],
  todayBg: teal[50],
  // Wire-block / holiday accent (red).
  block: status.error.solid,
  blockBg: status.error.bg,
};

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// OOO entries are tinted by reason. Known reasons get a stable color; anything
// else falls back to a hashed pick so the same reason always looks the same.
const REASON_PALETTE = [
  { c: "#2451e6", bg: "#eef2ff" }, // blue
  { c: "#0f9d6c", bg: "#e8f7f0" }, // green
  { c: "#c47e0a", bg: "#fdf3e3" }, // amber
  { c: "#7c3aed", bg: "#f3e8ff" }, // purple
  { c: "#0e7a8a", bg: "#e3f4f6" }, // teal
  { c: "#b1430f", bg: "#fbe9e0" }, // rust
];
const KNOWN_REASONS = {
  pto: 0,
  vacation: 0,
  sick: 2,
  appt: 4,
  appointment: 4,
  personal: 3,
  holiday: 1,
};
const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
export const reasonStyle = (reason) => {
  const key = String(reason || "").trim().toLowerCase();
  if (key && key in KNOWN_REASONS) return REASON_PALETTE[KNOWN_REASONS[key]];
  return REASON_PALETTE[key ? hash(key) % REASON_PALETTE.length : 0];
};

// ── Dates / timezones ────────────────────────────────────────────────────────
export const dayKey = (date) => (date ? format(date, "yyyy-MM-dd") : null);
export const ymd = (date) => (date ? format(date, "yyyy-MM-dd") : null);

const parseKey = (key) => {
  const m = String(key || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

// The calendar date (yyyy-MM-dd) an instant falls on, in the given IANA tz
// (falls back to the browser tz). en-CA formats as yyyy-MM-dd.
export const dateKeyInTz = (iso, tz) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  }
};

export const formatTimeInTz = (iso, tz) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz || undefined,
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return format(d, "h:mm a");
  }
};

// Short timezone abbreviation (e.g. "EDT") for a tz at a given instant.
export const tzAbbr = (iso, tz) => {
  if (!tz) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date(iso || Date.now()));
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
};

// All calendar day-keys an OOO entry spans (inclusive), in the entry's tz.
export const entryDayKeys = (entry) => {
  const startKey = dateKeyInTz(entry?.start, entry?.timezone);
  const endKey = dateKeyInTz(entry?.end, entry?.timezone);
  const start = parseKey(startKey);
  const end = parseKey(endKey) || start;
  if (!start) return [];
  const keys = [];
  let d = start;
  // Guard against runaway ranges (bad data): cap at ~1 year of days.
  for (let i = 0; d <= end && i < 400; i += 1) {
    keys.push(dayKey(d));
    d = addDays(d, 1);
  }
  return keys;
};

export const entryLabel = (entry) =>
  entry?.userFullName || entry?.userEmail || "Out of office";

export const entryTimeLabel = (entry) => {
  if (entry?.allDay) return "All day";
  const tz = entry?.timezone;
  return `${formatTimeInTz(entry?.start, tz)} – ${formatTimeInTz(entry?.end, tz)}`;
};

// Bucket OOO entries by day-key → entry[]; and bank holidays by day-key → holiday.
export const bucketCoverage = (entries, holidays) => {
  const byDay = new Map();
  (Array.isArray(entries) ? entries : []).forEach((e) => {
    entryDayKeys(e).forEach((k) => {
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k).push(e);
    });
  });
  byDay.forEach((list) =>
    list.sort((a, b) => entryLabel(a).localeCompare(entryLabel(b))),
  );
  const holidayByDay = new Map();
  (Array.isArray(holidays) ? holidays : []).forEach((h) => {
    const k = dateKeyInTz(h?.holidayDate, "UTC") || String(h?.holidayDate || "").slice(0, 10);
    if (k) holidayByDay.set(k, h);
  });
  return { byDay, holidayByDay };
};

// Friendly label for a tz value (e.g. "America/Chicago" → "Chicago").
export const tzShortLabel = (tz) =>
  String(tz || "").split("/").pop()?.replace(/_/g, " ") || tz;
